/**
 * RecordCategoryPicker — pole „Kategoria" (NOWA oś klasyfikacji, JEDNA na
 * rekord, tabela `record_categories` — migracja 0123). Structural mirror
 * `SystemModuleCategoryField.tsx` (pole „Kategoria" modułów systemowych,
 * `system_module_categories`), ale:
 *  - inny endpoint: `GET /api/record-categories?kartoteka=<key>`,
 *  - bez koloru/ikony (`record_categories` nie ma tych kolumn — świadomy
 *    kontrast do porzuconego `system_module_categories`),
 *  - bez zwracania `null` gdy lista pusta — kartoteka bez kategorii ma po
 *    prostu pustą listę wyboru (dropdown z samym placeholderem), pole
 *    zostaje widoczne (o widoczności pola w formularzu decyduje layout, nie
 *    ten komponent).
 *
 * Caller (renderer core-fieldu `category_id` w configu silnika formatek danej
 * kartoteki, np. `contractorEngineConfig.tsx`) trzyma `categoryId` w swoim
 * form state i przekazuje przez `value`. Pusty wybór w edit mode = `null`
 * (czyść kategorię).
 */

import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { Dropdown } from '@/components/Dropdown';
import { Field } from '@/components/kartoteki/CoreFieldLayout';
import { api } from '@/lib/api';

export interface RecordCategory {
  id: string;
  kartotekaKey: string;
  name: string;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface RecordCategoryPickerProps {
  /** Klucz kartoteki (`record_categories.kartoteka_key`) — np. `'contractors'`. */
  kartotekaKey: string;
  value: string | null;
  onChange: (next: string | null) => void;
  editable: boolean;
  /** Prefiks data-testid (np. `contractor-category`). */
  testId?: string;
}

export function RecordCategoryPicker({
  kartotekaKey,
  value,
  onChange,
  editable,
  testId,
}: RecordCategoryPickerProps) {
  const { t } = useTranslation();

  const categoriesQ = useQuery({
    queryKey: ['record-categories', kartotekaKey],
    queryFn: async () => {
      const res = await api.get<{ categories: RecordCategory[] }>('/record-categories', {
        params: { kartoteka: kartotekaKey },
      });
      return res.data.categories;
    },
    staleTime: 60_000,
  });

  const categories = categoriesQ.data ?? [];
  const selected = value ? (categories.find((c) => c.id === value) ?? null) : null;

  if (!editable) {
    return (
      <Field label={t('tenant.recordCategories.picker.label')}>
        <div
          className="flex min-h-[34px] items-center rounded-lg border border-transparent bg-bg-secondary px-3 py-1.5 text-sm"
          data-testid={testId}
        >
          {selected ? (
            <span className="text-text-primary">{selected.name}</span>
          ) : (
            <span className="text-text-tertiary">{t('common.notSet')}</span>
          )}
        </div>
      </Field>
    );
  }

  return (
    <Field label={t('tenant.recordCategories.picker.label')}>
      <Dropdown
        value={value ?? ''}
        onChange={(v) => onChange(v || null)}
        data-testid={testId}
        className="w-full rounded-lg border border-border-secondary bg-bg-primary px-3 py-1.5 text-sm"
        options={[
          { value: '', label: t('tenant.recordCategories.picker.placeholder') },
          ...categories.map((c) => ({ value: c.id, label: c.name })),
        ]}
      />
    </Field>
  );
}
